import { useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '@/lib/api/client';
import { PdfReader } from './PdfReader';

// BCBC 2024 Navigator — full-bleed pdf.js reader with a COLLAPSIBLE FLOATING
// TOOLBAR overlaid on the document, so search / contents / quick-refs /
// bookmarks / page-jump are reachable from anywhere in the 1,906 pages —
// collapsed it's a single button + live page chip. Shared by admin + portal.

interface QuickRef { topic: string; value: string; section: string; keywords: string }
interface TocNode { t: string; p: number; c: TocNode[] }
interface SearchHit { page: number; snippet: string; count: number; mode?: 'phrase' | 'terms'; doc?: string; short?: string }
interface LibDoc { slug: string; title: string; short: string; url: string; tocUrl: string | null }

const QUICK_REFS: QuickRef[] = [
  { topic: 'Stairs — max riser height', value: '7-7/8" (200 mm) private stairs', section: '9.8.4.1', keywords: 'stair rise riser step' },
  { topic: 'Stairs — min run (tread)', value: '10" (255 mm) private stairs', section: '9.8.4.2', keywords: 'stair run tread depth step' },
  { topic: 'Handrail height', value: '34"–42" (865–1070 mm) above nosing', section: '9.8.7.4', keywords: 'handrail rail height' },
  { topic: 'Guard height — interior', value: '35-7/16" (900 mm) where drop < 1800 mm', section: '9.8.8.3', keywords: 'guard rail height interior balcony' },
  { topic: 'Guard height — exterior/high', value: '42" (1070 mm) where drop > 1800 mm', section: '9.8.8.3', keywords: 'guard rail height deck exterior' },
  { topic: 'Guard required at', value: 'drop over 24" (600 mm)', section: '9.8.8.1', keywords: 'guard required drop landing deck' },
  { topic: 'Guard openings', value: 'no opening passes a 4" (100 mm) sphere', section: '9.8.8.5', keywords: 'guard baluster spacing opening picket' },
  { topic: 'Bedroom egress window', value: '≥ 0.35 m² opening, no dimension < 15" (380 mm)', section: '9.9.10.1', keywords: 'egress window bedroom escape' },
  { topic: 'Ceiling height — living/dining', value: '7\'-7" (2.3 m) over ≥ 75% of area', section: '9.5.3.1', keywords: 'ceiling height room' },
  { topic: 'Smoke alarms', value: 'each storey + each bedroom or hallway serving them', section: '9.10.19', keywords: 'smoke alarm detector fire' },
  { topic: 'Joist/beam bearing — min', value: '1-1/2" (38 mm) wood/steel; 3-1/2" (89 mm) masonry', section: '9.23.8', keywords: 'joist beam bearing' },
  { topic: 'Vapour barrier', value: 'warm side of insulation', section: '9.25.4', keywords: 'vapour barrier poly insulation' },
  { topic: 'Attic access hatch', value: '≥ 0.32 m², min dimension 545 mm', section: '9.18.4 / 9.19.2', keywords: 'attic access hatch crawl' },
];


// Jobsite slang & jargon → what the code calls it. `search` feeds the full-text
// search so a carpenter can find code sections using the words crews actually use.
interface GlossEntry { term: string; aliases: string; meaning: string; code: string; search: string }
const GLOSSARY: GlossEntry[] = [
  { term: 'Rat run', aliases: 'strongback ratrun', meaning: 'Continuous brace run across the tops of ceiling joists or truss webs to keep them straight and evenly spaced.', code: 'strapping / bracing', search: 'strapping' },
  { term: 'Mudsill', aliases: 'sill plate sole', meaning: 'The pressure-treated plate anchored to the top of the foundation that framing sits on.', code: 'sill plate', search: 'sill plate' },
  { term: 'J-bolt', aliases: 'anchor bolt', meaning: 'Hooked bolt cast into concrete to hold the sill plate down.', code: 'anchor / anchorage', search: 'anchor' },
  { term: 'Pony wall', aliases: 'cripple wall knee wall', meaning: 'A short framed wall — on a foundation, or under rafters in an attic.', code: 'cripple wall / knee wall', search: 'cripple' },
  { term: 'King stud', aliases: 'king', meaning: 'Full-height stud nailed beside an opening, running plate to plate.', code: 'stud (at openings)', search: 'studs at openings' },
  { term: 'Jack stud', aliases: 'trimmer jack', meaning: 'Shortened stud under the ends of a header, carrying its load down.', code: 'jack stud / trimmer', search: 'lintel support' },
  { term: 'Cripple', aliases: 'cripple stud', meaning: 'Short stud above a header or below a sill — fills in, carries little load.', code: 'cripple stud', search: 'cripple' },
  { term: 'Header', aliases: 'lintel beam over opening', meaning: 'The beam over a door or window opening.', code: 'lintel', search: 'lintel' },
  { term: 'Rim joist', aliases: 'band joist rim board header joist', meaning: 'The joist that caps the ends of the floor joists around the perimeter.', code: 'header joist / rim joist', search: 'header joist' },
  { term: 'Blocking', aliases: 'blocks solid bridging', meaning: 'Short pieces between joists or studs — for support, fire stopping, or backing.', code: 'blocking / fire stop', search: 'blocking' },
  { term: 'X-bridging', aliases: 'cross bridging herringbone', meaning: 'Diagonal pairs between joists that stiffen the floor.', code: 'cross bridging', search: 'bridging' },
  { term: 'Birdsmouth', aliases: 'birds mouth seat cut', meaning: 'Notch in a rafter where it seats on the wall plate.', code: 'rafter bearing', search: 'rafter' },
  { term: 'Collar tie', aliases: 'collar', meaning: 'Horizontal member connecting opposite rafters partway up.', code: 'collar tie', search: 'collar tie' },
  { term: 'Ridge', aliases: 'ridge board ridge beam', meaning: 'The top member rafters meet at. A ridge BEAM carries load; a ridge BOARD just spaces rafters.', code: 'ridge board / ridge beam', search: 'ridge' },
  { term: 'Lookout', aliases: 'outrigger ladder framing', meaning: 'Cantilevered framing carrying the roof overhang at a gable end.', code: 'lookout', search: 'roof overhang' },
  { term: 'Let-in brace', aliases: 'wind brace diagonal brace', meaning: 'Diagonal brace notched into studs to keep a wall square.', code: 'wall bracing', search: 'bracing' },
  { term: 'Scab', aliases: 'scabbed sister fish plate', meaning: 'A piece nailed alongside a member to reinforce or extend it.', code: 'splice / reinforcement', search: 'splice' },
  { term: 'Sistering', aliases: 'sister joist', meaning: 'Fastening a new joist alongside a weak or cut one.', code: 'reinforcement of joists', search: 'notching and drilling' },
  { term: 'Crowning', aliases: 'crown crown up', meaning: 'Sighting lumber for its bow and installing crown-up so load flattens it.', code: '(good practice — not a code term)', search: 'joists' },
  { term: 'OC', aliases: 'on centre on center centres', meaning: 'Spacing measured centre-to-centre — 16" OC, 24" OC.', code: 'spacing (o.c.)', search: 'spacing' },
  { term: 'Ledger', aliases: 'ledger board deck ledger', meaning: 'Board bolted to the structure that carries deck or floor joists.', code: 'ledger', search: 'ledger' },
  { term: 'Hanger', aliases: 'joist hanger bucket', meaning: 'Metal connector carrying a joist off a ledger or beam.', code: 'joist hanger / framing connector', search: 'joist hanger' },
  { term: 'Teleposts', aliases: 'jack post adjustable column', meaning: 'Adjustable steel column under a beam.', code: 'adjustable steel column', search: 'columns' },
  { term: 'Frost wall', aliases: 'frost footing', meaning: 'Foundation wall taken below frost depth so it can not heave.', code: 'foundation depth (frost)', search: 'frost' },
  { term: 'Weeping tile', aliases: 'perimeter drain big-o', meaning: 'Perforated drain pipe around the footing keeping water off the foundation.', code: 'foundation drainage', search: 'drainage' },
  { term: 'Damp-proofing', aliases: 'tar black goo', meaning: 'Coating on the outside of the foundation below grade.', code: 'dampproofing', search: 'dampproofing' },
  { term: 'Vapour barrier', aliases: 'poly vb 6 mil', meaning: 'Polyethylene sheet on the warm side of insulation.', code: 'vapour barrier', search: 'vapour barrier' },
  { term: 'Tyvek', aliases: 'house wrap wrb building paper', meaning: 'Sheathing membrane behind cladding (brand name for the category).', code: 'sheathing membrane / WRB', search: 'sheathing membrane' },
  { term: 'Strapping', aliases: 'furring 1x3', meaning: 'Thin members fastened across framing to carry finishes or vent cladding.', code: 'furring / strapping', search: 'furring' },
  { term: 'Rainscreen', aliases: 'rain screen gap', meaning: 'Vented cavity behind cladding — required in most of coastal BC.', code: 'capillary break / cladding cavity', search: 'capillary' },
  { term: 'Fire stop', aliases: 'firestop fire blocking draft stop', meaning: 'Blocking that stops fire spreading through concealed cavities.', code: 'fire stop', search: 'fire stop' },
  { term: 'Egress window', aliases: 'escape window bedroom window', meaning: 'Bedroom window big enough to climb out of in a fire.', code: 'egress — windows as means of escape', search: 'egress' },
  { term: 'Stringer', aliases: 'stair stringer carriage', meaning: 'The cut or housed member that carries stair treads.', code: 'stair stringer', search: 'stringer' },
  { term: 'Nosing', aliases: 'stair nose overhang', meaning: 'The front edge of a tread projecting past the riser.', code: 'nosing', search: 'nosing' },
  { term: 'Winders', aliases: 'winder pie steps', meaning: 'Wedge-shaped treads that turn a stair without a landing.', code: 'winders', search: 'winder' },
  { term: 'Baluster', aliases: 'picket spindle', meaning: 'The vertical infill members of a guard.', code: 'guard opening limits', search: 'guard' },
  { term: 'Glulam', aliases: 'glue lam beam', meaning: 'Beam glued up from dimension lumber laminations.', code: 'glued-laminated timber', search: 'glued-laminated' },
  { term: 'LVL', aliases: 'lvl engineered beam microlam', meaning: 'Laminated veneer lumber — engineered beam stock.', code: 'structural composite lumber', search: 'structural composite' },
  { term: 'I-joist', aliases: 'tji silent floor', meaning: 'Engineered joist with flanges and an OSB web.', code: 'prefabricated wood I-joist', search: 'I-joist' },
  { term: 'OSB', aliases: 'chipboard aspenite waferboard', meaning: 'Oriented strand board sheathing.', code: 'OSB / waferboard', search: 'waferboard' },
  { term: 'T&G', aliases: 'tongue and groove 3/4 t g', meaning: 'Tongue-and-groove edges on subfloor sheets.', code: 'tongue and groove subfloor', search: 'subfloor' },
  { term: 'Kerf', aliases: 'saw kerf', meaning: 'The slot a saw blade cuts — matters when a cut weakens a member.', code: 'notching / drilling limits', search: 'notch' },
  { term: 'Green lumber', aliases: 'wet lumber unseasoned', meaning: 'Lumber above 19% moisture — shrinks as it dries.', code: 'moisture content', search: 'moisture content' },
  { term: 'PT', aliases: 'pressure treated brown treated wolmanized', meaning: 'Preservative-treated lumber for wet or ground-contact locations.', code: 'preservative-treated wood', search: 'preservative' },
  { term: 'Six-mil poly', aliases: '6 mil poly under slab', meaning: 'Polyethylene sheet under concrete slabs and as vapour barrier.', code: 'ground cover / vapour barrier', search: 'polyethylene' },
];

// Standard construction vocabulary — same shape, no "slang" badge.
const VOCAB: GlossEntry[] = [
  { term: 'Joist', aliases: 'floor joist ceiling joist', meaning: 'Horizontal framing member carrying a floor or ceiling.', code: 'joist', search: 'joist' },
  { term: 'Rafter', aliases: 'common rafter', meaning: 'Sloped member carrying the roof from ridge to wall plate.', code: 'rafter', search: 'rafter' },
  { term: 'Truss', aliases: 'roof truss girder truss', meaning: 'Engineered triangulated frame replacing rafters and ceiling joists; never cut without approval.', code: 'roof truss', search: 'truss' },
  { term: 'Stud', aliases: 'wall stud 2x4 2x6', meaning: 'Vertical framing member of a wall.', code: 'stud', search: 'stud' },
  { term: 'Top plate', aliases: 'double plate cap plate', meaning: 'Horizontal member(s) on top of a wall, tying it together and carrying joists/trusses.', code: 'top plate', search: 'top plate' },
  { term: 'Bottom plate', aliases: 'sole plate', meaning: 'Horizontal member at the base of a framed wall.', code: 'bottom plate', search: 'bottom plate' },
  { term: 'Beam', aliases: 'girder carrying beam', meaning: 'Horizontal structural member carrying loads from joists or walls to posts/foundations.', code: 'beam / girder', search: 'beam' },
  { term: 'Bearing wall', aliases: 'loadbearing load bearing', meaning: 'Wall that carries load from above (roof/floors), not just its own weight.', code: 'loadbearing wall', search: 'loadbearing' },
  { term: 'Span', aliases: 'clear span allowable span', meaning: 'The distance a member bridges between supports; span tables set the limits.', code: 'span (tables)', search: 'span' },
  { term: 'Footing', aliases: 'strip footing pad', meaning: 'The widened concrete base spreading foundation loads into the soil.', code: 'footing', search: 'footing' },
  { term: 'Foundation wall', aliases: 'concrete wall stem wall', meaning: 'Wall from footing to sill carrying the structure and retaining soil.', code: 'foundation wall', search: 'foundation wall' },
  { term: 'Slab-on-grade', aliases: 'slab basement floor', meaning: 'Concrete floor cast directly over prepared ground.', code: 'slab / floor-on-ground', search: 'floors-on-ground' },
  { term: 'Backfill', aliases: 'backfilling', meaning: 'Soil placed back against a foundation — walls need strength/support first.', code: 'backfill', search: 'backfill' },
  { term: 'Grade', aliases: 'finished grade ground level', meaning: 'Ground level against the building; drives clearances and drainage slopes.', code: 'grade', search: 'grade' },
  { term: 'Pier', aliases: 'pad concrete pier', meaning: 'Isolated foundation element under a post.', code: 'pier', search: 'pier' },
  { term: 'Column', aliases: 'post', meaning: 'Vertical member carrying beam loads down to a footing.', code: 'column / post', search: 'column' },
  { term: 'Sheathing', aliases: 'sheeting roof deck wall sheathing', meaning: 'Panels (plywood/OSB) over framing that brace it and back the cladding/roofing.', code: 'sheathing', search: 'sheathing' },
  { term: 'Subfloor', aliases: 'sub floor deck', meaning: 'Structural panel layer over floor joists under the finish floor.', code: 'subfloor', search: 'subfloor' },
  { term: 'Cladding', aliases: 'siding exterior finish', meaning: 'The exterior weather-facing finish — siding, stucco, brick veneer.', code: 'cladding', search: 'cladding' },
  { term: 'Flashing', aliases: 'head flashing step flashing', meaning: 'Metal or membrane directing water out of joints — over openings, at roof/wall lines.', code: 'flashing', search: 'flashing' },
  { term: 'Eave', aliases: 'eaves overhang', meaning: 'The lower roof edge projecting past the wall.', code: 'eave', search: 'eave' },
  { term: 'Soffit', aliases: 'soffits', meaning: 'The underside finish of the eave, usually vented.', code: 'soffit', search: 'soffit' },
  { term: 'Fascia', aliases: 'fascia board', meaning: 'Vertical trim at the roof edge carrying gutters.', code: 'fascia', search: 'fascia' },
  { term: 'Gable', aliases: 'gable end', meaning: 'The triangular wall section under a sloped roof end.', code: 'gable', search: 'gable' },
  { term: 'Hip', aliases: 'hip roof', meaning: 'Sloped external roof corner where two planes meet.', code: 'hip', search: 'hip' },
  { term: 'Valley', aliases: 'valley rafter', meaning: 'Internal roof corner collecting water from two planes.', code: 'valley', search: 'valley' },
  { term: 'Pitch', aliases: 'slope roof slope', meaning: 'Roof steepness as rise per 12 of run (4/12, 8/12).', code: 'slope', search: 'slope' },
  { term: 'Dormer', aliases: 'dormers', meaning: 'A roofed projection with a window breaking through the roof plane.', code: 'dormer', search: 'dormer' },
  { term: 'Cantilever', aliases: 'overhang bump out', meaning: 'Framing projecting beyond its support — limits set by span rules.', code: 'cantilever', search: 'cantilever' },
  { term: 'Shear wall', aliases: 'braced wall panel', meaning: 'Wall designed to resist lateral (wind/earthquake) loads.', code: 'braced wall panel', search: 'braced wall' },
  { term: 'Dead load', aliases: 'dl', meaning: 'Permanent weight — structure and finishes themselves.', code: 'dead load', search: 'dead load' },
  { term: 'Live load', aliases: 'll snow load', meaning: 'Variable loads — people, furniture, snow.', code: 'live load / snow load', search: 'live load' },
  { term: 'Deflection', aliases: 'bounce sag', meaning: 'How much a member bends under load; limits keep floors solid and ceilings uncracked.', code: 'deflection', search: 'deflection' },
  { term: 'Insulation / R-value', aliases: 'rsi r22 r-value batts', meaning: 'Resistance to heat flow — code sets minimum RSI/R per assembly and climate zone.', code: 'thermal resistance (RSI)', search: 'thermal resistance' },
  { term: 'Air barrier', aliases: 'air sealing airtight', meaning: 'Continuous layer stopping air leakage through the envelope (distinct from the vapour barrier).', code: 'air barrier system', search: 'air barrier' },
  { term: 'HRV', aliases: 'heat recovery ventilator air exchanger', meaning: 'Ventilation unit exchanging stale inside air with fresh, recovering heat.', code: 'heat recovery ventilator', search: 'heat recovery' },
  { term: 'Guard', aliases: 'guardrail railing', meaning: 'Barrier preventing falls from a raised area — height and opening rules apply.', code: 'guard', search: 'guard' },
  { term: 'Handrail', aliases: 'hand rail grab rail', meaning: 'Graspable rail alongside stairs or ramps.', code: 'handrail', search: 'handrail' },
  { term: 'Landing', aliases: 'stair landing', meaning: 'Platform at the top, bottom, or turn of a stair.', code: 'landing', search: 'landing' },
  { term: 'Riser / Tread', aliases: 'rise run steps', meaning: 'Riser = vertical face of a step; tread = the part you stand on.', code: 'rise and run', search: 'riser' },
  { term: 'Headroom', aliases: 'head height clearance', meaning: 'Vertical clearance over stairs or in rooms.', code: 'headroom', search: 'headroom' },
  { term: 'Fire separation', aliases: 'fire rating rated wall', meaning: 'Assembly restricting fire spread — e.g. between a garage or suite and the house.', code: 'fire separation', search: 'fire separation' },
  { term: 'Smoke alarm', aliases: 'smoke detector', meaning: 'Required on every storey and serving every bedroom, interconnected.', code: 'smoke alarm', search: 'smoke alarm' },
  { term: 'Secondary suite', aliases: 'basement suite in-law suite adu', meaning: 'Self-contained dwelling unit within a house — its own code section.', code: 'secondary suite', search: 'secondary suite' },
  { term: 'Party wall', aliases: 'common wall demising', meaning: 'Shared wall between two dwellings — fire and sound rules apply.', code: 'party wall', search: 'party wall' },
  { term: 'Permit / Inspection', aliases: 'building permit inspector sign off', meaning: 'Municipal approval to build, with staged inspections (footing, framing, insulation, final).', code: 'building permit (Division C)', search: 'permit' },
];

// One combined lookup: slang entries carry a badge, vocabulary does not.
const ALL_TERMS: (GlossEntry & { slang?: boolean })[] = [
  ...GLOSSARY.map(g => ({ ...g, slang: true })),
  ...VOCAB,
].sort((a, b) => a.term.localeCompare(b.term));

const BOOKMARK_KEY = 'bccodeBookmarks';

function TocBranch({ nodes, depth, onGo }: { nodes: TocNode[]; depth: number; onGo: (p: number) => void }) {
  const [open, setOpen] = useState<Set<number>>(new Set(depth === 0 ? nodes.map((_, i) => i) : []));
  return (
    <div className={depth > 0 ? 'ml-3 border-l border-gray-100 pl-2' : ''}>
      {nodes.map((n, i) => (
        <div key={i}>
          <div className="flex items-center gap-1">
            {n.c.length > 0 ? (
              <button
                onClick={() => setOpen(prev => { const nx = new Set(prev); if (nx.has(i)) nx.delete(i); else nx.add(i); return nx; })}
                className="w-5 h-6 flex items-center justify-center text-gray-400 flex-shrink-0"
              >
                <span className={`material-symbols-outlined text-sm transition-transform ${open.has(i) ? 'rotate-90' : ''}`}>chevron_right</span>
              </button>
            ) : <span className="w-5 flex-shrink-0" />}
            <button
              onClick={() => onGo(n.p)}
              className={`flex-1 text-left py-1 text-xs hover:text-[#C77D3F] truncate ${depth <= 1 ? 'font-semibold text-gray-800' : 'text-gray-600'}`}
              title={`${n.t} — page ${n.p}`}
            >
              {n.t} <span className="text-gray-300">· {n.p}</span>
            </button>
          </div>
          {n.c.length > 0 && open.has(i) && <TocBranch nodes={n.c} depth={depth + 1} onGo={onGo} />}
        </div>
      ))}
    </div>
  );
}

export function BCCodeViewer({ canUpload }: { canUpload: boolean }) {
  const [status, setStatus] = useState<{ exists: boolean; url: string; hasToc?: boolean } | null>(null);
  const [library, setLibrary] = useState<LibDoc[]>([]);
  const [docSlug, setDocSlug] = useState('bcbc');
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);          // jump target for the reader
  const [jumpNonce, setJumpNonce] = useState(0);
  const [livePage, setLivePage] = useState(1);  // where the reader actually is
  const [pageInput, setPageInput] = useState('1');
  const [query, setQuery] = useState(() => new URLSearchParams(window.location.search).get('q') || '');
  const [hits, setHits] = useState<SearchHit[] | null>(null);
  // Search sources — a doc slug in this set is EXCLUDED from search results
  const [mutedDocs, setMutedDocs] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem('refMutedDocs') || '[]')); } catch { return new Set(); }
  });
  const toggleMuted = (slug: string) => setMutedDocs(prev => {
    const next = new Set(prev);
    if (next.has(slug)) next.delete(slug); else next.add(slug);
    localStorage.setItem('refMutedDocs', JSON.stringify([...next]));
    return next;
  });
  const [searching, setSearching] = useState(false);
  const [toc, setToc] = useState<TocNode[]>([]);
  const [tab, setTab] = useState<'search' | 'contents' | 'quickref' | 'glossary' | 'bookmarks' | 'askai'>('search');
  // CR #205 — Ask Claude about the code
  const [aiQ, setAiQ] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiLog, setAiLog] = useState<{ q: string; a: string }[]>([]);
  const askAi = async () => {
    const q = aiQ.trim();
    if (!q || aiBusy) return;
    setAiBusy(true); setAiError('');
    try {
      const r = await apiClient.post('/reference/bccode/ask', { question: q }, { timeout: 120000 });
      setAiLog(l => [{ q, a: String(r.data?.data?.answer || '') }, ...l].slice(0, 10));
      setAiQ('');
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setAiError(err?.response?.data?.error || 'AI request failed — try again.');
    } finally { setAiBusy(false); }
  };
  const [panelOpen, setPanelOpen] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [bookmarks, setBookmarks] = useState<{ name: string; page: number }[]>(() => {
    try { return JSON.parse(localStorage.getItem(BOOKMARK_KEY) || '[]'); } catch { return []; }
  });
  const fileRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const load = () => {
    apiClient.get('/reference/bccode/status').then(r => setStatus(r.data.data)).catch(() => setStatus({ exists: false, url: '' }));
    apiClient.get('/reference/library').then(r => setLibrary(r.data.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const currentDoc = library.find(d => d.slug === docSlug) || library[0] || null;

  // Per-document table of contents
  useEffect(() => {
    setToc([]);
    if (currentDoc?.tocUrl) {
      fetch(currentDoc.tocUrl).then(x => x.json()).then(setToc).catch(() => {});
    }
  }, [currentDoc?.tocUrl]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 3) { setHits(null); setSearching(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      apiClient.get(`/reference/library/search?q=${encodeURIComponent(q)}`)
        .then(r => setHits(r.data.data))
        .catch(() => setHits([]))
        .finally(() => setSearching(false));
    }, 350);
  }, [query]);

  const visibleHits = (hits || []).filter(h => !mutedDocs.has(h.doc || 'bcbc'));

  const goTo = (p: number, doc?: string) => {
    if (doc && doc !== docSlug) setDocSlug(doc);
    const n = Math.max(1, Math.floor(p));
    setPage(n);
    setJumpNonce(x => x + 1); // re-jump even to the same page
    setPageInput(String(n));
    // On phones, collapse the toolbar so the destination is actually visible
    if (window.innerWidth < 1024) setPanelOpen(false);
  };

  const quickRefMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return QUICK_REFS;
    return QUICK_REFS.filter(r => r.topic.toLowerCase().includes(q) || r.keywords.includes(q) || r.section.includes(q));
  }, [query]);

  // Slang → code-term matches for the current query ("rat run" → strapping)
  const glossMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return ALL_TERMS.filter(g =>
      g.term.toLowerCase().includes(q) || g.aliases.includes(q) || g.code.toLowerCase().includes(q));
  }, [query]);

  const addBookmark = () => {
    const name = window.prompt(`Bookmark name for page ${livePage}?`);
    if (!name?.trim()) return;
    const next = [...bookmarks, { name: name.trim(), page: livePage }];
    setBookmarks(next);
    localStorage.setItem(BOOKMARK_KEY, JSON.stringify(next));
  };
  const removeBookmark = (i: number) => {
    const next = bookmarks.filter((_, idx) => idx !== i);
    setBookmarks(next);
    localStorage.setItem(BOOKMARK_KEY, JSON.stringify(next));
  };

  const uploadPdf = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await apiClient.post('/reference/bccode', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      load();
    } catch { window.alert('Upload failed — is it a PDF under 200 MB?'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  if (!status) return <div className="h-40 animate-pulse rounded-xl bg-gray-100" />;

  if (!status.exists) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-10 text-center space-y-3">
        <span className="material-symbols-outlined text-gray-300 text-5xl">menu_book</span>
        <p className="font-semibold text-gray-700">BC Building Code PDF not loaded yet</p>
        {canUpload ? (
          <label className="inline-flex items-center gap-2 h-10 px-5 rounded-lg bg-primary text-white text-sm font-semibold cursor-pointer hover:opacity-90">
            <span className="material-symbols-outlined text-base">{uploading ? 'progress_activity' : 'upload_file'}</span>
            {uploading ? 'Uploading & indexing…' : 'Upload Code PDF'}
            <input ref={fileRef} type="file" accept="application/pdf" className="sr-only" disabled={uploading}
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadPdf(f); }} />
          </label>
        ) : (
          <p className="text-sm text-gray-500">Ask the office to load it — then it appears here for everyone.</p>
        )}
      </div>
    );
  }

  // Highlight the phrase if present, else every individual word (all-words mode)
  const highlight = (snippet: string) => {
    const q = query.trim().toLowerCase();
    if (!q) return snippet;
    const terms = snippet.toLowerCase().includes(q) ? [q] : q.split(/\s+/).filter(t => t.length >= 2);
    if (!terms.length) return snippet;
    const pattern = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    const parts = snippet.split(new RegExp(`(${pattern})`, 'gi'));
    return (
      <>
        {parts.map((part, i) =>
          terms.includes(part.toLowerCase())
            ? <mark key={i} className="bg-[#C77D3F]/25 text-inherit rounded px-0.5">{part}</mark>
            : part
        )}
      </>
    );
  };

  const tabBtn = (k: typeof tab, icon: string, label: string) => (
    <button key={k} onClick={() => setTab(k)}
      className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-semibold rounded-md ${tab === k ? 'bg-white text-[#C77D3F] shadow-sm' : 'text-gray-500'}`}>
      <span className="material-symbols-outlined text-sm">{icon}</span>
      {label}
    </button>
  );

  return (
    <div className={fullscreen
      ? 'fixed inset-0 z-[60] bg-gray-200'
      : 'relative h-full min-h-[75vh] rounded-xl overflow-hidden border border-gray-200'}>

      {/* The document — full bleed */}
      <div className="absolute inset-0">
        <PdfReader
          key={currentDoc?.slug || 'bcbc'}
          url={currentDoc?.url || status.url}
          targetPage={page}
          jumpNonce={jumpNonce}
          highlightTerms={query.trim().length >= 3
            ? [query.trim(), ...query.trim().split(/\s+/)].filter(t => t.length >= 2)
            : []}
          onVisiblePage={p => { setLivePage(p); setPageInput(String(p)); }} />
      </div>

      {/* ── Floating toolbar ── */}
      {panelOpen ? (
        <div className="absolute left-2 top-2 bottom-2 z-20 w-[min(24rem,calc(100%-1rem))] flex flex-col rounded-2xl bg-white/95 backdrop-blur shadow-2xl border border-gray-200 overflow-hidden">
          {/* Header: search + collapse */}
          <div className="flex items-center gap-2 p-2 border-b border-gray-100">
            <div className="relative flex-1">
              <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-base">
                {searching ? 'progress_activity' : 'search'}
              </span>
              <input
                value={query}
                onChange={e => { setQuery(e.target.value); setTab('search'); }}
                placeholder="Search the whole code…"
                className="w-full h-10 pl-8 pr-8 rounded-xl border border-gray-200 bg-gray-50 text-sm focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/30"
              />
              {query && (
                <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                  <span className="material-symbols-outlined text-base">close</span>
                </button>
              )}
            </div>
            <button onClick={() => setPanelOpen(false)} title="Collapse toolbar — the doc stays where you are"
              className="h-10 w-10 rounded-xl border border-gray-200 text-gray-500 flex items-center justify-center hover:bg-gray-50 flex-shrink-0">
              <span className="material-symbols-outlined">left_panel_close</span>
            </button>
          </div>

          {/* Reference documents */}
          {library.length > 1 && (
            <div className="flex gap-1 mx-2 mt-2 overflow-x-auto scrollbar-none">
              {library.map(d => (
                <button key={d.slug} onClick={() => { setDocSlug(d.slug); setPage(1); setPageInput('1'); setJumpNonce(x => x + 1); }}
                  title={d.title}
                  className={`h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap flex-shrink-0 border ${docSlug === d.slug ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'bg-white text-gray-600 border-gray-200'}`}>
                  {d.short}
                </button>
              ))}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mx-2 mt-2">
            {tabBtn('search', 'search', 'Results')}
            {tabBtn('contents', 'toc', 'Index')}
            {tabBtn('quickref', 'bolt', 'Quick')}
            {tabBtn('glossary', 'forum', 'Slang')}
            {tabBtn('bookmarks', 'bookmark', 'Marks')}
            {tabBtn('askai', 'auto_awesome', 'Ask AI')}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {tab === 'search' && (
              query.trim().length < 3 ? (
                <p className="text-xs text-gray-400 p-2">Type at least 3 letters above — e.g. "stair riser", "egress", "R-22". Tap a result to jump to its page.</p>
              ) : (
                <>
                  {glossMatches.slice(0, 4).map((g, i) => (
                    <div key={`g${i}`} className="bg-white rounded-lg border border-[#2C1810]/20 px-3 py-2">
                      <p className="text-xs font-bold text-gray-900">{g.term}{(g as GlossEntry & { slang?: boolean }).slang && <span className="text-[10px] font-normal text-gray-400"> slang</span>}</p>
                      <p className="text-xs text-gray-600 leading-snug">{g.meaning}</p>
                      <button onClick={() => { setQuery(g.search); setTab('search'); }}
                        className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#C77D3F]">
                        <span className="material-symbols-outlined text-sm">manage_search</span>
                        In the code: "{g.code}" — search "{g.search}"
                      </button>
                    </div>
                  ))}
                  {quickRefMatches.length > 0 && quickRefMatches.length < QUICK_REFS.length && quickRefMatches.map((r, i) => (
                    <div key={`q${i}`} className="bg-[#FAF6F0] rounded-lg border border-[#C77D3F]/25 px-3 py-2">
                      <p className="text-xs font-semibold text-gray-900">{r.topic}</p>
                      <p className="text-sm text-[#C77D3F] font-bold">{r.value}</p>
                      <p className="text-[10px] text-gray-400">Quick ref · {r.section} · verify in code</p>
                    </div>
                  ))}
                  {/* Search-source toggles — mute a document (e.g. Simpson) to keep it out of results */}
                  {library.length > 1 && (hits || searching) && (
                    <div className="flex items-center gap-1.5 px-1 flex-wrap">
                      <span className="text-[10px] text-gray-400">Search in:</span>
                      {library.map(d => (
                        <button key={d.slug} onClick={() => toggleMuted(d.slug)}
                          title={mutedDocs.has(d.slug) ? `${d.title} is hidden from results — tap to include` : `Tap to hide ${d.title} from results`}
                          className={`h-6 px-2 rounded-full text-[10px] font-bold border ${mutedDocs.has(d.slug) ? 'bg-gray-100 text-gray-400 border-gray-200 line-through' : 'bg-[#2C1810] text-white border-[#2C1810]'}`}>
                          {d.short}
                        </button>
                      ))}
                    </div>
                  )}
                  {searching && !hits && <p className="text-xs text-gray-400 p-2">Searching the library…</p>}
                  {hits && visibleHits.length === 0 && !searching && (
                    <p className="text-xs text-gray-400 p-2">
                      {hits.length > 0 ? `All ${hits.length} matches are in hidden sources.` : `No pages contain "${query.trim()}".`}
                    </p>
                  )}
                  {visibleHits.map((h, i) => (
                    <button key={i} onClick={() => goTo(h.page, h.doc)}
                      className={`w-full text-left bg-white rounded-lg border px-3 py-2 hover:border-[#C77D3F]/50 ${livePage === h.page && (h.doc || 'bcbc') === docSlug ? 'border-[#C77D3F]' : 'border-gray-200'}`}>
                      <p className="text-[10px] font-bold text-[#C77D3F]">
                        {h.short && <span className="mr-1.5 px-1.5 py-0.5 rounded bg-[#2C1810] text-white">{h.short}</span>}
                        Page {h.page}{h.count > 1 ? ` · ${h.count}×` : ''}
                        {h.mode === 'terms' && <span className="ml-1.5 font-medium text-gray-400">all words</span>}
                      </p>
                      <p className="text-xs text-gray-600 leading-snug">{highlight(h.snippet)}</p>
                    </button>
                  ))}
                </>
              )
            )}
            {tab === 'contents' && (
              toc.length
                ? <TocBranch nodes={toc} depth={0} onGo={goTo} />
                : <p className="text-xs text-gray-400 p-2">Contents not indexed — re-upload the PDF to build it.</p>
            )}
            {tab === 'quickref' && (
              <>
                <p className="text-[10px] text-gray-400 px-1">Common values — always confirm in the code text.</p>
                {QUICK_REFS.map((r, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg border border-gray-100 px-3 py-2">
                    <p className="text-xs font-semibold text-gray-900">{r.topic}</p>
                    <p className="text-sm text-[#C77D3F] font-bold">{r.value}</p>
                    <p className="text-[10px] text-gray-400">Section {r.section}</p>
                  </div>
                ))}
              </>
            )}
            {tab === 'glossary' && (
              <>
                <p className="text-[10px] text-gray-400 px-1">Every term A–Z — jobsite slang and proper vocabulary. Tap the ochre line to search the code's own wording. Type in the search box to filter.</p>
                {(glossMatches.length && query.trim().length >= 2 ? glossMatches : ALL_TERMS).map((g, i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <p className="text-xs font-bold text-gray-900">{g.term}{(g as GlossEntry & { slang?: boolean }).slang && <span className="text-[10px] font-normal text-gray-400"> slang</span>}</p>
                    <p className="text-xs text-gray-600 leading-snug">{g.meaning}</p>
                    <button onClick={() => { setQuery(g.search); setTab('search'); }}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-[#C77D3F]">
                      <span className="material-symbols-outlined text-sm">manage_search</span>
                      Code term: "{g.code}"
                    </button>
                  </div>
                ))}
              </>
            )}
            {tab === 'askai' && (
              <div className="space-y-2 p-1">
                <p className="text-xs text-gray-500">Ask Claude about the building code — cites sections, gives metric + imperial. Answers are advisory: always verify in the code text.</p>
                <div className="flex gap-1.5">
                  <input
                    value={aiQ}
                    onChange={e => setAiQ(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') askAi(); }}
                    placeholder="e.g. Max deck guard opening? Egress window size?"
                    className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-gray-300 text-sm bg-white focus:outline-none focus:border-[#C77D3F]"
                  />
                  <button
                    onClick={askAi}
                    disabled={aiBusy || !aiQ.trim()}
                    className="h-10 px-3 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-50 flex items-center gap-1"
                  >
                    <span className={`material-symbols-outlined text-base ${aiBusy ? 'animate-spin' : ''}`}>{aiBusy ? 'progress_activity' : 'auto_awesome'}</span>
                    Ask
                  </button>
                </div>
                {aiError && <p className="text-xs text-red-600">{aiError}</p>}
                {aiBusy && <p className="text-xs text-gray-400 animate-pulse">Asking Claude…</p>}
                {aiLog.map((m, i) => (
                  <div key={i} className="bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <p className="text-xs font-bold text-gray-900 mb-1">Q: {m.q}</p>
                    <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{m.a}</p>
                  </div>
                ))}
                {aiLog.length === 0 && !aiBusy && (
                  <p className="text-[11px] text-gray-400 p-1">Answers appear here — newest first, kept for this visit only.</p>
                )}
              </div>
            )}
            {tab === 'bookmarks' && (
              bookmarks.length === 0
                ? <p className="text-xs text-gray-400 p-2">No bookmarks yet — use the bookmark button in the bottom bar.</p>
                : bookmarks.map((b, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <button onClick={() => goTo(b.page)} className="flex-1 text-left text-xs font-semibold text-gray-800">
                      {b.name} <span className="text-gray-400 font-normal">· p.{b.page}</span>
                    </button>
                    <button onClick={() => removeBookmark(i)} className="text-gray-300 hover:text-red-500">
                      <span className="material-symbols-outlined text-base">close</span>
                    </button>
                  </div>
                ))
            )}
          </div>

          {/* Footer: page jump · bookmark · fullscreen (+ admin replace) */}
          <div className="border-t border-gray-100 p-2 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <input
                value={pageInput}
                onChange={e => setPageInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') goTo(Number(pageInput) || 1); }}
                inputMode="numeric"
                className="w-16 h-9 px-2 rounded-lg border border-gray-300 text-sm text-center"
              />
              <button onClick={() => goTo(Number(pageInput) || 1)}
                className="h-9 px-3 rounded-lg bg-primary text-white text-sm font-semibold">Go</button>
              <span className="text-[10px] text-gray-400 truncate max-w-[5rem]">{currentDoc?.short || 'BCBC'}</span>
              <div className="ml-auto flex items-center gap-1">
                <button onClick={addBookmark} title={`Bookmark page ${livePage}`}
                  className="h-9 w-9 rounded-lg border border-gray-200 text-[#C77D3F] flex items-center justify-center hover:bg-[#FAF6F0]">
                  <span className="material-symbols-outlined text-lg">bookmark_add</span>
                </button>
                <button onClick={() => setFullscreen(f => !f)} title={fullscreen ? 'Exit full screen' : 'Full screen'}
                  className="h-9 w-9 rounded-lg border border-gray-200 text-gray-600 flex items-center justify-center hover:bg-gray-50">
                  <span className="material-symbols-outlined text-lg">{fullscreen ? 'fullscreen_exit' : 'fullscreen'}</span>
                </button>
              </div>
            </div>
            {canUpload && (
              <div className="flex items-center gap-3">
                <label className="block text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">
                  Replace BCBC PDF…
                  <input type="file" accept="application/pdf" className="sr-only" disabled={uploading}
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadPdf(f); }} />
                </label>
                <label className="block text-[10px] text-gray-400 cursor-pointer hover:text-gray-600">
                  + Add reference PDF (catalog, spec)…
                  <input type="file" accept="application/pdf" className="sr-only" disabled={uploading}
                    onChange={async e => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      const title = window.prompt('Name for this reference (e.g. "Simpson Connectors 2025")?', f.name.replace(/\.pdf$/i, ''));
                      if (!title?.trim()) return;
                      setUploading(true);
                      try {
                        const form = new FormData();
                        form.append('title', title.trim());
                        form.append('file', f);
                        await apiClient.post('/reference/library', form, { headers: { 'Content-Type': 'multipart/form-data' } });
                        load();
                      } catch { window.alert('Upload failed — PDF under 200 MB?'); }
                      finally { setUploading(false); }
                    }} />
                </label>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Collapsed: floating button cluster — reachable from anywhere in the doc */
        <div className="absolute left-2 top-2 z-20 flex items-center gap-1.5">
          <button
            onClick={() => setPanelOpen(true)}
            title="Open navigator (search, contents, bookmarks)"
            className="h-11 w-11 rounded-2xl bg-[#C77D3F] text-white shadow-xl flex items-center justify-center active:scale-95 transition-transform"
          >
            <span className="material-symbols-outlined">manage_search</span>
          </button>
          <button
            onClick={() => setPanelOpen(true)}
            className="h-8 px-2.5 rounded-full bg-white/95 border border-gray-200 shadow text-xs font-bold text-gray-700"
            title="Current page — tap to open navigator"
          >
            p. {livePage}
          </button>
          <button onClick={() => setFullscreen(f => !f)} title={fullscreen ? 'Exit full screen' : 'Full screen'}
            className="h-8 w-8 rounded-full bg-white/95 border border-gray-200 shadow text-gray-600 flex items-center justify-center">
            <span className="material-symbols-outlined text-base">{fullscreen ? 'fullscreen_exit' : 'fullscreen'}</span>
          </button>
        </div>
      )}
    </div>
  );
}
